import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Plus, Search, Eye, Edit, Trash2, Monitor, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { physicalMachineApiService, PhysicalMachine } from '../services/physicalMachineApi';
import { PermissionGate } from './ui/PermissionGate';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';

export function PhysicalMachineListingPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [machines, setMachines] = useState<PhysicalMachine[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const ROUTE = '/physical-machines';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const branchId = selectedBranch || user?.defaultBranchId;
        const data = await physicalMachineApiService.getMachines(branchId);
        setMachines(data);
      } catch (e) {
        // toasted
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedBranch, user?.defaultBranchId]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return machines.filter(m =>
      m.machineCode?.toLowerCase().includes(term) ||
      m.machineName?.toLowerCase().includes(term) ||
      (m.erpCode || '').toLowerCase().includes(term) ||
      (m.machineModel || '').toLowerCase().includes(term) ||
      (m.machineEff || '').toLowerCase().includes(term) ||
      (m.usedFor || '').toLowerCase().includes(term) ||
      (m.smp || '').toLowerCase().includes(term)
    );
  }, [machines, searchTerm]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  const handleNew = () => navigate('/physical-machines/new');
  const handleEdit = (m: PhysicalMachine) => navigate(`/physical-machines/${m.id}/edit`);
  const handleView = (m: PhysicalMachine) => navigate(`/physical-machines/${m.id}/view`);
  const handleDelete = async (m: PhysicalMachine) => {
    if (!m.id) return;
    if (window.confirm(`Delete machine ${m.machineName}?`)) {
      try {
        await physicalMachineApiService.deleteMachine(String(m.id));
        setMachines(prev => prev.filter(x => x.id !== m.id));
      } catch (e) {
        // toasted
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading machines...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Physical Machine Management</CardTitle>
                <p className="text-gray-600 text-sm">Manage physical machines and assignments</p>
              </div>
            </div>
            <PermissionGate route={ROUTE} action="create">
              <Button onClick={handleNew} className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg">
                <Plus className="h-4 w-4 mr-2" />
                New Machine
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search machines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">{filtered.length} of {machines.length} machines</div>
          </div>

          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">Physical Machine Code</TableHead>
                  <TableHead className="font-semibold text-primary-800">Physical Machine Name</TableHead>
                  <TableHead className="font-semibold text-primary-800">ERP Code</TableHead>
                  <TableHead className="font-semibold text-primary-800">Model</TableHead>
                  <TableHead className="font-semibold text-primary-800">Machine Eff</TableHead>
                  <TableHead className="font-semibold text-primary-800">Used For</TableHead>
                  <TableHead className="font-semibold text-primary-800">SMP</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No machines found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((m) => (
                    <TableRow key={m.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{m.machineCode}</TableCell>
                      <TableCell>{m.machineName}</TableCell>
                      <TableCell>{m.erpCode || '-'}</TableCell>
                      <TableCell>{m.machineModel || '-'}</TableCell>
                      <TableCell>{m.machineEff || '-'}</TableCell>
                      <TableCell>{m.usedFor || '-'}</TableCell>
                      <TableCell>{m.smp || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-center">
                          <PermissionGate route={ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(m)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(m)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(m)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


